import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {from, Observable, of} from 'rxjs';
import * as ShelfTalkerActions from './shelf-talkers.actions';
import {Action, select, Store} from '@ngrx/store';
import {catchError, map, mergeMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {IAdvancedFilterGroup} from '../../settings-new/models/general/advanced-filter-group.interface';
import {IFacetCounts, TypeSenseSearchResponse} from '../../../shared/shared-models/type-sense/type-sense-types';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {
  createFacetCountsMap,
  mapTypeSenseObjectToGeneric,
} from '../../../shared/shared-utils/typesense-utils/typesense.utils';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {TypeSenseService} from '../../../shared/shared-services/type-sense/type-sense.service';
import {
  selectPrintOtherShelfTalkers,
  selectShelfTalkersPaginationData,
  selectShelfTalkersSortMethodForStoreId,
} from './shelf-talkers.selectors';
import {CollectionSharedStockService} from '../../../shared/shared-services/firebase/collection-shared-stock.service';
import {
  subQueryStockShelfTalkers,
  subQueryStockShelfTalkersExceptions,
} from '../../../shared-utilities/queries-old/sub-query-stock-2';
import {GridUtils} from '../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {withoutPrintedShelfTalkers} from '../utils/shelf-talker-print-lists.utils';
import {selectLineColoursBySelectedStore} from '../../core/store-shared/shared.selectors';
import {
  IFirebaseLabelDesignsData,
} from '../../../shared/shared-components/components/shared-label-designer/models/label-interfaces';
import {
  CollectionStoresSettingsService,
} from '../../../shared/shared-services/firebase/collection-stores-settings.service';
import {
  path_stores_storeId_settings_shelf_talkers_settings,
  path_users_userId_settings_stores_storeId_selected_layout,
} from '../../../shared/shared-services/database-paths';
import {CollectionGlobalService} from '../../../shared/shared-services/firebase/collection-global.service';
import {
  CollectionUsersSettingsService,
} from '../../../shared/shared-services/firebase/collection-users-settings.service';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import {TLineColour} from '../../settings-new/models';
import {createGlobalPaths} from '../../../shared/shared-services/database-paths.global';

@Injectable()
export class ShelfTalkersEffects {

  // ====================================================================================================
  //
  // ====================================================================================================

  getTypesenseStockItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.getShelfTalkerStockItems),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        this.collectionSharedStockService.getStockItemsCast(store, subQueryStockShelfTalkers, 'objectID').pipe(
          map((stockItemsRaw: StockItem[]) => {
            const stockItems = stockItemsRaw
              .filter((item: StockItem) => !item._tags.includes('shelf_talker_exception'))
              .map((item: StockItem): StockItem => ({
                ...item,
                storeId: store.storeId,
                store: store.name,
                stockId: item.stockId,
              }));
            return ShelfTalkerActions.getShelfTalkerStockItemsSuccess({stockItems, store});
          }),
          catchError((error) => {
            return of(ShelfTalkerActions.getShelfTalkerStockItemsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
              store,
            }));
          }),
        ),
      ),
    ),
  );

  getTypesenseStockItemsExceptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.getShelfTalkersStockItemsExceptions),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        this.collectionSharedStockService.getStockItemsCast(store, subQueryStockShelfTalkersExceptions, 'objectID').pipe(
          map((stockItemsRaw: StockItem[]) => {
            const stockItems = stockItemsRaw
              .map((item: StockItem): StockItem => ({
                ...item,
                storeId: store.storeId,
                store: store.name,
              }));
            return ShelfTalkerActions.getShelfTalkersStockItemsExceptionsSuccess({stockItems, store});
          }),
          catchError((error) => {
            return of(ShelfTalkerActions.getShelfTalkersStockItemsExceptionsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            );
          }),
        ),
      ),
    ),
  );


  getOtherShelfTalkerStockItems$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ShelfTalkerActions.getOtherShelfTalkerStockItems,
        ShelfTalkerActions.getOtherItemsTypesenseShelfTalkersSearchResultsWithStoreId,
      ),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(selectPrintOtherShelfTalkers)),
        this.store.pipe(select(selectLineColoursBySelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, shelfTalker, lineColours, user]) =>
        this.typeSenseService.searchStockItems(
          shelfTalker.paginationData?.searchedValue ?? '',
          1,
          shelfTalker.paginationData?.pageSize ?? 20,
          store,
          {
            code: {value: true, name: 'Code'},
            desc: {value: true, name: 'Description'},
            suppCode: {value: true, name: 'suppCode'},
            barcode: {value: true, name: 'barcode'},
            binL: {value: true, name: 'binL'},
            regularSuppCode: {value: true, name: 'regularSuppCode'},
          },
          action.filters,
          undefined,
          (action as { advancedFilters?: IAdvancedFilterGroup }).advancedFilters,
        ).pipe(
          mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
            const updatedPaginationData: IPaginationData = {
              totalItems: stockItemsRaw.found,
              pageSize: shelfTalker.paginationData?.pageSize ?? 20,
              currentPage: 1,
              searchedValue: shelfTalker.paginationData?.searchedValue ?? '',
            };
            const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
            const facetCounts: IFacetCounts = createFacetCountsMap(stockItemsRaw.facet_counts);
            let stockItems: StockItem[] = mappedStockItems.map((item: StockItem) => ({
              ...item,
              storeId: store.storeId,
              store: store.name,
            }));

            stockItems = GridUtils.applyLineColoursToStockManager(
              lineColours as TLineColour,
              stockItems,
            );

            return [
              ShelfTalkerActions.getOtherShelfTalkerStockItemsSuccess({
                stockItems,
                paginationData: updatedPaginationData,
                store,
                facetCounts,
                filter: action.filters,
              }),
            ];
          }),
          catchError((error) =>
            of(
              ShelfTalkerActions.getOtherShelfTalkerStockItemsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
                store,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getPaginationResultsForAddStockItem$ = createEffect(() => this.actions$.pipe(
    ofType(ShelfTalkerActions.getPaginationResultsForShelfTalkerStockItem),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(selectShelfTalkersPaginationData)),
      this.store.pipe(select(selectShelfTalkersSortMethodForStoreId)),
      this.store.pipe(select(selectLineColoursBySelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    mergeMap(([action, store, paginationData, sortMethod, lineColours, user]) =>
      from(this.typeSenseService.searchStockItems(
        paginationData?.searchedValue ?? '',
        action.pageNumber,
        paginationData?.pageSize ?? 20,
        store,
        {
          code: {value: true, name: 'Code'},
          desc: {value: true, name: 'Description'},
        },
        {
          ['_tags']: {
            ['in_shelf_talkers']: {name: 'in_shelf_talkers', value: true},
          },
        } as Record<string, ISearchableFields>,
        sortMethod !== '' ? `${sortMethod}, 59:asc` : '59:asc',
      )).pipe(
        mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
          const paginationData: IPaginationData = {
            totalItems: stockItemsRaw.found,
            pageSize: stockItemsRaw?.request_params.per_page ?? 20,
            currentPage: action.pageNumber,
            searchedValue: '',
          };
          const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
          let stockItems: StockItem[] = mappedStockItems.map((item: StockItem) => ({
            ...item,
            storeId: store.storeId,
            store: store.name,
          }));

          stockItems = GridUtils.applyLineColoursToStockManager(
            lineColours as TLineColour,
            stockItems,
          );
          return [ShelfTalkerActions.getShelfTalkerStockItemsSuccess({
            stockItems,
            store,
          }),
          ];
        }),
        catchError((error) => {
            return of(ShelfTalkerActions.getShelfTalkerStockItemsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
              store,
            }));
          },
        ),
      ),
    ),
  ));

  getOtherPaginationResultsForAddStockItem$ = createEffect(() => this.actions$.pipe(
    ofType(ShelfTalkerActions.getOtherPaginationResultsForShelfTalkerStockItem),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(selectPrintOtherShelfTalkers)),
      this.store.pipe(select(selectLineColoursBySelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    mergeMap(([action, store, {paginationData, filter}, lineColours, user]) =>
      from(this.typeSenseService.searchStockItems(
        paginationData?.searchedValue ?? '',
        action.pageNumber,
        paginationData?.pageSize ?? 20,
        store,
        {
          code: {value: true, name: 'Code'},
          desc: {value: true, name: 'Description'},
          suppCode: {value: true, name: 'suppCode'},
          barcode: {value: true, name: 'barcode'},
          binL: {value: true, name: 'binL'},
        },
        filter,
        undefined,
        action.advancedFilters,
      )).pipe(
        mergeMap((stockItemsRaw: TypeSenseSearchResponse<StockItem>) => {
          const paginationData: IPaginationData = {
            totalItems: stockItemsRaw.found,
            pageSize: stockItemsRaw?.request_params.per_page ?? 20,
            currentPage: action.pageNumber,
            searchedValue: stockItemsRaw?.request_params?.q ?? '',
          };
          const mappedStockItems = mapTypeSenseObjectToGeneric<StockItem>(stockItemsRaw);
          const facetCounts: IFacetCounts = createFacetCountsMap(stockItemsRaw.facet_counts);
          let stockItems: StockItem[] = mappedStockItems.map((item: StockItem) => ({
            ...item,
            storeId: store.storeId,
            store: store.name,
          }));

          stockItems = GridUtils.applyLineColoursToStockManager(
            lineColours as TLineColour,
            stockItems,
          );
          return [ShelfTalkerActions.getOtherShelfTalkerStockItemsSuccess({
            stockItems,
            paginationData,
            store,
            facetCounts: facetCounts,
            filter,
          }),
          ];
        }),
        catchError((error) => {
            return of(ShelfTalkerActions.getOtherShelfTalkerStockItemsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
              store,
            }));
          },
        ),
      ),
    ),
  ));

  removeShelfTalkerTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.removeShelfTalkerTag),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, selectedStore, user]) => {
        const {stockItems, updateStorage} = action;
        const store = action.store ?? selectedStore;
        if (Object.keys(stockItems).length > 0) {
          const updatedStockItems: Record<string, StockItem> = Object.values(stockItems).reduce((acc, item) => {
            const documentId = item.stockId || item.code;
            acc[documentId] = {
              ...item,
              ['_tags']: (item._tags ?? []).filter((tag: string) => tag !== 'in_shelf_talkers'),
            };
            return acc;
          }, {} as Record<string, StockItem>);
          const tagsMapping: Record<string, string[]> = Object.values(stockItems).reduce((acc, item) => {
            acc[item.stockId || item.code] = (item._tags ?? []).filter((tag: string) => tag !== 'in_shelf_talkers');
            return acc;
          }, {} as Record<string, string[]>);
          return from(this.collectionSharedStockService.updateStockTags(tagsMapping, store)).pipe(
            map(() =>
              ShelfTalkerActions.removeShelfTalkerTagSuccess({
                removedStockItems: updatedStockItems,
                store,
                updateStorage,
              }),
            ),
            catchError((error) => {
                if (updateStorage) {
                  this.revertOptimisticPrintedItems(store.storeId, stockItems);
                }
                return of(ShelfTalkerActions.removeShelfTalkerTagFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                    userId: user.id,
                  },
                  store,
                }));
              },
            ),
          );
        } else {
          return of(ShelfTalkerActions.removeShelfTalkerTagSuccess({removedStockItems: {}, store, updateStorage}));
        }
      }),
    ),
  );

  removeShelfTalkerTagSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShelfTalkerActions.removeShelfTalkerTagSuccess),
        tap(({removedStockItems, store, updateStorage}) => {
          if (updateStorage) {
            const key = `printed-shelf-talkers-${store.storeId}`;
            const existing = localStorage.getItem(key);
            const existingData = existing ? JSON.parse(existing) : {};
            const timestamp = Date.now();
            const updatedItems: Record<string, StockItem & { printed: number }> = {};
            Object.keys(removedStockItems).forEach((stockId) => {
              updatedItems[stockId] = {
                ...removedStockItems[stockId],
                printed: timestamp,
              };
            });
            const mergedData = {...existingData, ...updatedItems};
            localStorage.setItem(key, JSON.stringify(mergedData));
          }
        }),
      ),
    {dispatch: false},
  );

  removeShelfTalkerTagException$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.removeShelfTalkerExceptionsTag),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        const {stockItems, updateStorage} = action;
        if (Object.keys(stockItems).length > 0) {
          const updatedStockItems: Record<string, StockItem> = Object.values(stockItems).reduce((acc, item) => {
            acc[item.code] = {...item, ['_tags']: item._tags.filter((tag: string) => tag !== 'shelf_talker_exception')};
            return acc;
          }, {} as Record<string, StockItem>);
          const tagsMapping: Record<string, string[]> = Object.values(stockItems).reduce((acc, item) => {
            acc[item.code] = item._tags.filter(tag => tag !== 'shelf_talker_exception');
            return acc;
          }, {} as Record<string, string[]>);
          return from(this.collectionSharedStockService.updateStockTags(tagsMapping, store)).pipe(
            map(() =>
              ShelfTalkerActions.removeShelfTalkerExceptionsTagSuccess({
                removedStockItems: updatedStockItems,
                store,
                updateStorage,
              }),
            ),
            catchError((error) => {
                return of(ShelfTalkerActions.removeShelfTalkerExceptionsTagFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                    userId: user.id,
                  },
                  store,
                }));
              },
            ),
          );
        } else {
          return of(ShelfTalkerActions.removeShelfTalkerExceptionsTagSuccess({
            removedStockItems: {},
            store,
            updateStorage,
          }));
        }
      }),
    ),
  );

  addShelfTalkerExceptionsTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.addShelfTalkerExceptionsTag),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) => {
        const {stockItems} = action;
        if (Object.keys(stockItems).length > 0) {
          const updatedStockItems: Record<string, StockItem> = Object.values(stockItems).reduce((acc, item) => {
            acc[item.code] = {...item, ['_tags']: [...item._tags, 'shelf_talker_exception']};
            return acc;
          }, {} as Record<string, StockItem>);
          const tagsMapping: Record<string, string[]> = Object.values(stockItems).reduce((acc, item) => {
            acc[item.code] = [...item._tags, 'shelf_talker_exception'];
            return acc;
          }, {} as Record<string, string[]>);
          return from(this.collectionSharedStockService.updateStockTags(tagsMapping, store)).pipe(
            map(() =>
              ShelfTalkerActions.addShelfTalkerExceptionsTagSuccess({
                store,
              }),
            ),
            catchError((error) => {
                return of(ShelfTalkerActions.addShelfTalkerExceptionsTagFailure({
                  error: {
                    ...error,
                    action: JSON.stringify(action),
                    actionType: action.type,
                    originalError: error,
                    storeIds: [store.storeId],
                    userId: user.id,
                  },
                  store,
                }));
              },
            ),
          );
        } else {
          return of(ShelfTalkerActions.addShelfTalkerExceptionsTagSuccess({
            store,
          }));
        }
      }),
    ),
  );


  setShelfTalkersSortMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.setShelfTalkersSortMethod),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([{sortMethod}, store]) => {
        return of(ShelfTalkerActions.setShelfTalkersSortMethodSuccess({sortMethod, store}));
      }),
    ),
  );

  getTypesenseShelfTalkersSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.getTypesenseShelfTalkersSearchResults),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(selectShelfTalkersPaginationData)),
      ),
      switchMap(([{idField}, store, paginationData]) => {
        const newPaginationData = {...paginationData, searchedValue: idField, currentPage: 1};
        return of(ShelfTalkerActions.getTypesenseShelfTalkersSearchResultsWithStoreId({
          paginationData: newPaginationData,
          store,
        }));
      }),
    ),
  );

  getOtherItemsTypesenseShelfTalkersSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.getOtherItemsTypesenseShelfTalkersSearchResults),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(selectPrintOtherShelfTalkers)),
      ),
      switchMap(([{idField, filters, advancedFilters}, store, {paginationData}]) => {
        const newPaginationData = {...paginationData, searchedValue: idField, currentPage: 1};
        return of(ShelfTalkerActions.getOtherItemsTypesenseShelfTalkersSearchResultsWithStoreId({
          paginationData: newPaginationData,
          store,
          filters,
          advancedFilters,
        }));
      }),
    ),
  );

  setPageSizeForShelfTalkers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.setPageSizeForShelfTalkers),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(selectShelfTalkersPaginationData)),
      ),
      map(([{pageSize}, store, {currentPage}]) => {
        return ShelfTalkerActions.setPageSizeForShelfTalkersAtStoreId({
          pageSize: pageSize,
          store: store,
          pageNumber: currentPage,
        });
      }),
    ),
  );

  setOtherPageSizeForShelfTalkers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.setOtherPageSizeForShelfTalkers),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(selectPrintOtherShelfTalkers)),
      ),
      map(([{pageSize}, store, {paginationData}]) => {
        return ShelfTalkerActions.setOtherPageSizeForShelfTalkersAtStoreId({
          paginationData: {...paginationData, pageSize},
          store: store,
          pageNumber: paginationData?.currentPage ?? 1,
        });
      }),
    ),
  );


  getShelfTalkerSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShelfTalkerActions.getShelfTalkerSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      switchMap(([action, store, user]) =>
        from((async (): Promise<Action> => {
          let settingsData = await this.collectionStoresSettingsService.getDocument<IFirebaseLabelDesignsData>(
            path_stores_storeId_settings_shelf_talkers_settings(store.storeId),
          );

          if (!settingsData || !settingsData.layouts?.length) {
            settingsData = await this.collectionGlobalService.getDocument<IFirebaseLabelDesignsData>(
              createGlobalPaths().defaultShelftalkerLabels(),
            );
          }

          if (!settingsData?.layouts?.length) {
            throw new Error('No shelf talker layouts are available for this store.');
          }

          const storeData: string = await this.collectionStoresSettingsService.getDocument(createSharedPaths().setStoreId(store.storeId).storeInfo());

          const userSelectedLayout = await this.collectionUsersSettingsService.getDocument<{ layout: string }>(
            path_users_userId_settings_stores_storeId_selected_layout(user.id, store.storeId),
          );

          const selectedLayout = userSelectedLayout?.layout
            ? userSelectedLayout.layout
            : settingsData?.defaultLayout
              ? settingsData.defaultLayout
              : settingsData.layouts[0].name;

          return ShelfTalkerActions.getShelfTalkerSettingsSuccess({
            shelfTalkerSettings: {
              companyLogo: storeData?.['companyLogo'],
              companyName: storeData?.['companyName'],
              defaultLayout: settingsData.defaultLayout,
              selectedLayout,
              layouts: settingsData.layouts,
            },
            store,
          });
        })()).pipe(
          catchError((error) => {
            return of(ShelfTalkerActions.getShelfTalkerSettingsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
              store,
            }));
          }),
        ),
      ),
    ),
  );

  setSelectedLayout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ShelfTalkerActions.setSelectedLayout),
        withLatestFrom(
          this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
          this.store.pipe(select(UserAccessSelectors.selectUser)),
        ),
        tap(([{selectedLayout}, store, user]) => {
          void this.collectionUsersSettingsService.setDocument(
            path_users_userId_settings_stores_storeId_selected_layout(user.id, store.storeId), {layout: selectedLayout});
        }),
      ),
    {dispatch: false},
  );

  constructor(
    private actions$: Actions,
    private typeSenseService: TypeSenseService,
    private collectionSharedStockService: CollectionSharedStockService,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private collectionGlobalService: CollectionGlobalService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
    private readonly store: Store,
  ) {
  }

  /**
   * Removes an optimistic printed-list write when the shelf-talker tag could not be cleared.
   *
   * @param storeId - Store whose printed list was updated before the tag write.
   * @param failedItems - Rows that must return to To Print.
   */
  private revertOptimisticPrintedItems(storeId: string, failedItems: Record<string, StockItem>): void {
    const key = `printed-shelf-talkers-${storeId}`;
    const existing = localStorage.getItem(key);
    const existingData = existing ? JSON.parse(existing) as Record<string, StockItem> : {};
    localStorage.setItem(key, JSON.stringify(withoutPrintedShelfTalkers(existingData, failedItems)));
  }
}
